'use client';

import { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import styles from './professional-packs-column.module.css';
import basedGetUrlRequestLogedIn from '../../../util/apiRequests/basedGetUrlRequestLogedIn';

const createSlug = title => {
  if (!title) return 'item';
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single
    .trim();
};

function ItemRow({ index, item, avgPrice, onImgError }) {
  const intl = useIntl();
  const itemSlug = createSlug(item.title);
  const itemLink = `/es/l/${itemSlug}/${item.id}`; // Construct link from id
  const itemPrice = item.price ? `${item.price}` : '';

  return (
    <li className={styles.itemRow}>
      <div className={styles.itemIndex} aria-hidden="true">
        {index}
      </div>

      <div className={styles.itemMain}>
        <a href={itemLink} target="_blank" rel="noreferrer" className={styles.itemName}>
          {item.title}
        </a>
        <a href={itemLink} target="_blank" rel="noreferrer" className={styles.viewListings}>
          {intl.formatMessage({ id: 'professionalPacksColumn.viewListings' })}
        </a>
      </div>

      <div className={styles.itemPrice} aria-label={`Precio medio ${itemPrice} dólares`}>
        <span className={styles.priceLabel}>
          {intl.formatMessage({ id: 'professionalPacksColumn.avgRental' })}
        </span>
        <span className={styles.priceValue}>
          {item.currency === 'USD' ? '$ ' : '€ '}
          {itemPrice}
        </span>
      </div>

      <div className={styles.itemThumb}>
        <img
          src={item.image || '/placeholder.svg?height=80&width=80&query=gear%20thumbnail'}
          alt={item.title}
          loading="lazy"
          onError={onImgError}
        />
      </div>
    </li>
  );
}

export default function ProfessionalPacksColumn() {
  const intl = useIntl();
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPacks = async () => {
      try {
        const response = await basedGetUrlRequestLogedIn('/api/listings-with-pack');
        if (response && response.data) {
          setPacks(response.data);
        }
      } catch (error) {
        console.error('Error fetching packs:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPacks();
  }, []);

  if (loading) {
    return (
      <div className={styles.page}>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          {intl.formatMessage({ id: 'professionalPacksColumn.loadingPacks' })}
        </div>
      </div>
    );
  }

  // Sections (3, 4, 5 items) to mirror the screenshot
  const vlogger = packs.slice(0, 3);
  const youtubers = packs.slice(0, 4);
  const studio = packs.slice(0, 5);

  return (
    <div id={'myDiv'} className={styles.page}>
      <section className={styles.hero} aria-labelledby="hero-title">
        <h1 id="hero-title" className={styles.title}>
          {intl.formatMessage({ id: 'professionalPacksColumn.explorePacks' })}
        </h1>
        <p className={styles.subtitle}>
          {intl.formatMessage({ id: 'professionalPacksColumn.solutionsReady' })}
        </p>

        <div className={styles.steps} role="list">
          <div className={styles.stepCard} role="listitem">
            <div className={styles.stepNumber}>{'1.'}</div>
            <div className={styles.stepText}>
              {intl.formatMessage({ id: 'professionalPacksColumn.choosePack' })}
            </div>
          </div>
          <div className={styles.stepCard} role="listitem">
            <div className={styles.stepNumber}>{'2.'}</div>
            <div className={styles.stepText}>
              {intl.formatMessage({ id: 'professionalPacksColumn.checkAvailability' })}
            </div>
          </div>
          <div className={styles.stepCard} role="listitem">
            <div className={styles.stepNumber}>{'3.'}</div>
            <div className={styles.stepText}>
              {intl.formatMessage({ id: 'professionalPacksColumn.rentWithOneClick' })}
            </div>
          </div>
        </div>

        <p className={styles.contactLine}>
          {intl.formatMessage({ id: 'professionalPacksColumn.contactLine' })}
          <a
            href="https://aloka.app/es"
            target="_blank"
            rel="noreferrer"
            className={styles.contactLink}
          >
            {intl.formatMessage({ id: 'professionalPacksColumn.contactAloka' })}
          </a>
        </p>

        <hr className={styles.divider} />
      </section>

      {/* PACK VLOGGER */}
      {packs.map(({ pack, listings }) => (
        <section
          id={intl.formatMessage({ id: `packs.${pack.type}.name` })}
          className={styles.packSection}
          aria-labelledby="vlogger-title"
        >
          <h2 id="vlogger-title" className={styles.packHeading}>
            {pack.name}
            {intl.formatMessage({ id: `packs.${pack.type}.name` })}
          </h2>
          <p className={styles.packSubheading}>
            {' '}
            {intl.formatMessage({ id: `packs.${pack.type}.desc` })}
          </p>

          <ol className={styles.itemList}>
            {listings.map((item, i) => (
              <ItemRow key={`${item.id}-vlogger`} index={i + 1} item={item} />
            ))}
          </ol>

          {/* <a className={styles.cta} href="/s">
            {intl.formatMessage({ id: 'professionalPacksColumn.rentPack' })}
          </a> */}
        </section>
      ))}
    </div>
  );
}
